import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { StoreCard } from '../components/StoreCard'

interface Department {
  id: string
  name: string
}

interface Municipality {
  id: string
  name: string
  dept_id: string
}

interface Store {
  id: string
  name: string
  address: string
  zona?: string
  coverage_km?: number
  has_delivery: boolean
  phone?: string
  lat?: number
  lng?: number
}

function WrenchSVG() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="empty-svg" aria-hidden="true">
      <circle cx="40" cy="40" r="38" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M30 50 L48 32 M48 32 Q54 26 56 30 Q58 34 52 40 L34 58 Q30 62 26 58 Q22 54 26 50 Z"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="29" cy="53" r="3" fill="currentColor" />
    </svg>
  )
}

export function Home() {
  const [departments, setDepartments] = useState<Department[]>([])
  const [municipalities, setMunicipalities] = useState<Municipality[]>([])
  const [stores, setStores] = useState<Store[]>([])

  const [deptId, setDeptId] = useState('')
  const [muniId, setMuniId] = useState('')
  const [search, setSearch] = useState('')

  const [loadingDepts, setLoadingDepts] = useState(true)
  const [loadingStores, setLoadingStores] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<Department[]>('/api/departments')
      .then(setDepartments)
      .catch(err => setError(err.message))
      .finally(() => setLoadingDepts(false))
  }, [])

  useEffect(() => {
    setMuniId('')
    setMunicipalities([])
    if (!deptId) return
    api.get<Municipality[]>(`/api/departments/${deptId}/municipalities`)
      .then(setMunicipalities)
      .catch(() => setMunicipalities([]))
  }, [deptId])

  useEffect(() => {
    setLoadingStores(true)
    setError('')
    const params = new URLSearchParams()
    if (deptId) params.set('dept_id', deptId)
    if (muniId) params.set('muni_id', muniId)
    if (search) params.set('search', search)
    params.set('limit', '50')

    const qs = params.toString()
    api.get<Store[]>(`/api/stores${qs ? `?${qs}` : ''}`)
      .then(setStores)
      .catch(err => setError(err.message))
      .finally(() => setLoadingStores(false))
  }, [deptId, muniId, search])

  return (
    <main>
      {/* Hero header */}
      <div className="marketplace-header">
        <div className="marketplace-header-inner">
          <h1 className="marketplace-title">
            Ferreterías en <span className="hero-highlight">Guatemala</span>
          </h1>
          <p className="marketplace-sub">
            Encuentra tu ferretería, arma tu carrito y envía tu pedido por WhatsApp
          </p>

          <div className="hero-search-bar">
            <input
              type="search"
              className="filter-search"
              placeholder="🔍 Buscar ferretería..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              className="filter-select"
              value={deptId}
              onChange={e => setDeptId(e.target.value)}
              disabled={loadingDepts}
            >
              <option value="">Todos los departamentos</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={muniId}
              onChange={e => setMuniId(e.target.value)}
              disabled={!deptId || municipalities.length === 0}
            >
              <option value="">Todos los municipios</option>
              {municipalities.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Store grid */}
      <div className="section">
        <div className="section-inner">
          {error && <div className="alert alert-error">{error}</div>}

          {loadingStores ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 20 }}>
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="skeleton" style={{ height: 140, borderRadius: 16 }} />
              ))}
            </div>
          ) : stores.length === 0 ? (
            <div className="empty-state animate-in">
              <WrenchSVG />
              <p style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--ink-3)' }}>
                No hay ferreterías en tu zona
              </p>
              <p style={{ fontSize: '.9rem' }}>Intenta con otro departamento o quita los filtros</p>
              {(deptId || muniId || search) && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => { setDeptId(''); setMuniId(''); setSearch('') }}
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="stores-count">
                {stores.length} ferretería{stores.length !== 1 ? 's' : ''} encontrada{stores.length !== 1 ? 's' : ''}
              </p>
              <div className="store-grid">
                {stores.map((s, i) => (
                  <div key={s.id} className="animate-in" style={{ animationDelay: `${i * 40}ms` }}>
                    <StoreCard {...s} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">🔧 <strong>FerreMarket Guatemala</strong></div>
          <p className="footer-copy">Conectando ferreterías con compradores guatemaltecos vía WhatsApp.</p>
        </div>
      </footer>
    </main>
  )
}
